import type { LucideIcon } from "lucide-react";
import {
  CalendarClock,
  Calculator,
  Camera,
  Contact,
  Database,
  FileSignature,
  Globe,
  LineChart,
  ListChecks,
  Magnet,
  Megaphone,
  PhoneCall,
  Sparkles,
  Star,
  Video,
  Wrench,
} from "lucide-react";

/**
 * The tool library's categories (public.tool_categories, seeded by
 * 20260715120001) with the names and icons the app shows. Slugs and order are
 * pinned to the seed by toolCategories.test.ts. Names are sentence case (app
 * copy rule); the database keeps its own Title Case names, which the
 * normalizer matches case-insensitively. Icons are ours, never tool logos.
 */
export interface ToolCategoryMeta {
  slug: string;
  name: string;
  sortOrder: number;
  icon: LucideIcon;
}

export const OTHER_TOOL_CATEGORY = "other";

export const TOOL_CATEGORIES: readonly ToolCategoryMeta[] = [
  { slug: "crm", name: "CRM", sortOrder: 1, icon: Contact },
  { slug: "lead-generation", name: "Lead generation", sortOrder: 2, icon: Magnet },
  { slug: "idx-website", name: "IDX / website", sortOrder: 3, icon: Globe },
  {
    slug: "transaction-management-and-esignature",
    name: "Transaction management & eSignature",
    sortOrder: 4,
    icon: FileSignature,
  },
  { slug: "cma-market-analysis", name: "CMA / market analysis", sortOrder: 5, icon: LineChart },
  { slug: "mls-and-data", name: "MLS & data", sortOrder: 6, icon: Database },
  { slug: "marketing-and-content", name: "Marketing & content", sortOrder: 7, icon: Megaphone },
  { slug: "video-and-communication", name: "Video & communication", sortOrder: 8, icon: Video },
  { slug: "dialers-and-isa", name: "Dialers & ISA", sortOrder: 9, icon: PhoneCall },
  { slug: "team-ops-and-accountability", name: "Team ops & accountability", sortOrder: 10, icon: ListChecks },
  { slug: "showing-and-scheduling", name: "Showing & scheduling", sortOrder: 11, icon: CalendarClock },
  { slug: "back-office-and-accounting", name: "Back office & accounting", sortOrder: 12, icon: Calculator },
  { slug: "ai-and-automation", name: "AI & automation", sortOrder: 13, icon: Sparkles },
  { slug: "client-engagement-and-reviews", name: "Client engagement & reviews", sortOrder: 14, icon: Star },
  { slug: "photography-and-virtual-tours", name: "Photography & virtual tours", sortOrder: 15, icon: Camera },
  { slug: OTHER_TOOL_CATEGORY, name: "Other tools", sortOrder: 99, icon: Wrench },
];

const BY_SLUG = new Map(TOOL_CATEGORIES.map((c) => [c.slug, c]));
const OTHER = BY_SLUG.get(OTHER_TOOL_CATEGORY)!;

export function isToolCategorySlug(slug: unknown): slug is string {
  return typeof slug === "string" && BY_SLUG.has(slug);
}

export function toolCategoryMeta(slug: string | null | undefined): ToolCategoryMeta {
  return (slug && BY_SLUG.get(slug)) || OTHER;
}
